/**
 * CommonQR — Theme Engine Foundation
 * packages/shared/src/config/themeEngine.ts
 *
 * Core type definitions for the Theme-Aware Live UI Design & Customization Engine.
 * Phase 1 of 7. Do NOT modify the existing themes.ts — this file is additive.
 */

export type UIStyle =
  | 'minimal'
  | 'solid'
  | 'outline'
  | 'glass'
  | 'soft'
  | 'pill'
  | 'elevated'
  | 'neon'
  | 'elegant'
  | 'futuristic';

export type ThemeMood =
  | 'corporate'
  | 'luxury'
  | 'nature'
  | 'cyberpunk'
  | 'playful'
  | 'organic'
  | 'minimal'
  | 'cultural';

export interface ThemeDNA {
  mood: ThemeMood;
  contrast: 'light' | 'medium' | 'dark';
  density: 'compact' | 'comfortable' | 'spacious';
  recommendedUI: UIStyle;
  compatibleUI: UIStyle[];
  headingFont: string;
  bodyFont: string;
  buttonFont: string;
}

export interface ThemeColorProfile {
  textColor: string;
  buttonColor: string;
  overlayOpacity: number;
  luminanceTop: number;
  luminanceMid: number;
  luminanceBottom: number;
  contrastRatio: number;
}

export interface DesignTokens {
  colorText: string;
  colorTextMuted: string;
  colorButton: string;
  colorButtonText: string;
  colorBorder: string;
  colorOverlay: string;
  fontHeading: string;
  fontBody: string;
  fontButton: string;
  sizeProfileName: number;
  sizeCategory: number;
  sizeDescription: number;
  sizeButtonLabel: number;
  sizeMetadata: number;
  weightHeading: number;
  weightButton: number;
  buttonStyle: UIStyle;
  buttonRadius: number;
  buttonShadow: string;
  buttonBlur: number;
  buttonOpacity: number;
}

export interface UserCustomization {
  uiStyle?: UIStyle;
  fontFamily?: string;
  fontSize?: 'auto' | 'sm' | 'md' | 'lg';
  textColor?: string;
  buttonColor?: string;
  buttonStyle?: UIStyle;
  buttonRadius?: number;
  linkLayout?: 'wide' | 'compact' | 'icons';
  iconColor?: 'theme' | 'brand';
}

export const CSS_VAR_MAP = {
  colorText:       '--cqr-color-text',
  colorTextMuted:  '--cqr-color-muted',
  colorButton:     '--cqr-color-button',
  colorButtonText: '--cqr-color-button-text',
  colorBorder:     '--cqr-color-border',
  colorOverlay:    '--cqr-color-overlay',
  fontHeading:     '--cqr-font-heading',
  fontBody:        '--cqr-font-body',
  fontButton:      '--cqr-font-button',
  buttonStyle:     '--cqr-button-style',
  buttonRadius:    '--cqr-radius-button',
  buttonShadow:    '--cqr-shadow-button',
  buttonBlur:      '--cqr-blur',
  buttonOpacity:   '--cqr-opacity',
  sizeProfileName: '--cqr-size-name',
  sizeCategory:    '--cqr-size-category',
  sizeDescription: '--cqr-size-description',
  sizeButtonLabel: '--cqr-size-button',
  sizeMetadata:    '--cqr-size-meta',
  weightHeading:   '--cqr-weight-heading',
  weightButton:    '--cqr-weight-button',
} as const;

export type CSSVarName = typeof CSS_VAR_MAP[keyof typeof CSS_VAR_MAP];
